///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import {
  controls,
  awardType,
  awardRank,
  ltlAcessorialTypes,
  cellUnit
} from "./types";
import messages from "./messages";
import { formatDate } from "./laneCalculations";

const extendedAccesorialTypes = [
  { value: 1, label: "None" },
  ...ltlAcessorialTypes
];

export const ltlColumns = [
  {
    header: messages.primaryRef.defaultMessage,
    field: "primaryReference",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    requiredColumn: true
  },
  {
    header: messages.origCity.defaultMessage,
    field: "originCity",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.origSt.defaultMessage,
    field: "originState",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.origPostal.defaultMessage,
    field: "originPostalCode",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.origCountry.defaultMessage,
    field: "originCountry",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.destCity.defaultMessage,
    field: "destinationCity",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.destSt.defaultMessage,
    field: "destinationState",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.destPostal.defaultMessage,
    field: "destinationPostalCode",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.destCountry.defaultMessage,
    field: "destinationCountry",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },

  {
    header: messages.earliestShip.defaultMessage,
    field: "earliestShipDate",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    format: formatDate
  },
  {
    header: messages.latestDelivery.defaultMessage,
    field: "latestDeliveryDate",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    format: formatDate
  },
  {
    header: messages.weight.defaultMessage,
    field: "weight",
    cellUnit: cellUnit.none,
    unitLeft: true,
    right: true,
    control: controls.none,
    rangeFilter: true
  },
  {
    header: messages.weightUom.defaultMessage,
    field: "weightUOM",
    cellUnit: cellUnit.none,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.maxFreightClass.defaultMessage,
    field: "maxFreightClass",
    cellUnit: cellUnit.none,
    unitLeft: true,
    right: true,
    control: controls.none,
    rangeFilter: true
  },

  {
    header: messages.accType.defaultMessage,
    field: "accessorialTypes",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    format: value =>
      value && value.length
        ? value
            .filter(accessorialId => accessorialId !== 0)
            .map(
              accessorialId =>
                extendedAccesorialTypes.find(
                  ({ value }) => value === accessorialId
                ).label
            )
            .join(", ")
        : undefined
  },
  {
    header: messages.carrier.defaultMessage,
    field: "carrierName",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none
  },
  {
    header: messages.linehaul.defaultMessage,
    field: "lineHaul",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.fuel.defaultMessage,
    field: "fuelCost",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.acc.defaultMessage,
    field: "accessorial",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.totCost.defaultMessage,
    field: "totalCost",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.totalRev.defaultMessage,
    field: "totalRevenue",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.margin.defaultMessage,
    field: "margin",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.marginPercentWithSymbol.defaultMessage,
    field: "marginPercentage",
    cellUnit: cellUnit.percent,
    unitLeft: false,
    right: true,
    control: controls.none
  },
  {
    header: messages.awardRate.defaultMessage,
    field: "awardRate",
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    right: true,
    control: controls.none
  },
  {
    header: messages.awardVol.defaultMessage,
    field: "awardVolume",
    cellUnit: cellUnit.none,
    unitLeft: true,
    right: true,
    control: controls.none,
    rangeFilter: true
  },
  {
    header: messages.awardType.defaultMessage,
    field: "awardType",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    format: value =>
      value ? awardType.find(f => f.value === value).label : value
  },
  {
    header: messages.awardRank.defaultMessage,
    field: "awardRank",
    cellUnit: cellUnit.none,
    unitLeft: true,
    filter: true,
    left: true,
    control: controls.none,
    format: value =>
      value ? awardRank.find(f => f.value === value).label : value
  },
  {
    field: "lessThanTruckloadLaneId",
    header: "",
    control: controls.delete,
    isDraggable: false,
    columnChooserLabel: messages.delete.defaultMessage
  }
];

export default ltlColumns;
